import { useCallback, useEffect, useRef, useState } from 'react'

import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import type { TFunction } from 'i18next'
import type { ChangeEventHandler } from 'react'

import { Header, ProgressCircular, SearchInput, spacing } from '@cash-design-system/react'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { Modal } from '@cash-web/shared-feature-modal'
import { CustomerSupportSearchEnterText, CustomerSupportSearchStart } from '@squareup/customer-data-events-web'

import TelescopeIcon from '../../assets/telescope'
import TelescopeDarkIcon from '../../assets/telescope-dark'
import type { LinkAdapterProps } from '../../types/types'
import { MAX_DISPLAYED_RESULTS } from './constants'
import SearchResults from './SearchResults'
import type { SearchResult } from './types'

type SearchInputProps = {
  invert?: boolean
}

interface SearchProps<T> extends LinkAdapterProps<T> {
  data?: { results: SearchResult[]; total_result_count: number; error_message?: string }
  isLoading: boolean
  isError: boolean
  showMoreResults: boolean
  initialHash?: string
  invert?: boolean
  onQueryChange?: (query: string) => void
  recommendedArticles?: SearchResult[]
  t: TFunction
  recommendedArticleTitle?: 'Recently viewed' | 'Top articles'
}

const ContainerStyles = css`
  margin-top: ${spacing.smallGap};
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const ResultsContainer = styled.div`
  ${ContainerStyles}
  background: ${props => props.theme.colors.background};
  margin: 20px -28px 0;
`

const TopArticleHeader = styled(Header)`
  margin-top: ${spacing.xl};
`

const StyledSearchInput = styled(SearchInput)<SearchInputProps>`
  background: ${props => (props.invert ? props.theme.colors.searchBackground : props.theme.colors.background)};

  ::placeholder {
    color: ${props => (props.invert ? props.theme.colors.tertiaryLabel : 'default')};
  }
`

const LoadingContainer = styled.div`
  ${ContainerStyles}
  background: ${props => props.theme.colors.background};
  align-items: center;
  padding: ${spacing.l};
  gap: ${spacing.l};
`

const ModalContent = styled.div`
  margin-top: ${spacing.s};
  padding-bottom: ${spacing.xxxl};

  ${props => props.theme.breakpoints.tablet} {
    padding-bottom: 0;
  }
`

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: ${spacing.xxl} 0;
`

const Search = <T,>({
  data,
  isLoading,
  isError,
  showMoreResults,
  initialHash,
  invert = false,
  onQueryChange,
  recommendedArticles,
  Link,
  t,
  recommendedArticleTitle = 'Top articles',
}: SearchProps<T>) => {
  const logCdfEvent = useLogCashWebCdfEvent()
  const [captureIntent, setCaptureIntent] = useState(false)
  const [readOnlyInputValue, setReadOnlyInputValue] = useState('')
  const [query, setQuery] = useState('')
  const [openModal, setOpenSearchModal] = useState(false)
  const { darkMode } = useTheme()

  useEffect(() => {
    if (initialHash && initialHash.length > 0) {
      setQuery(initialHash)
      setReadOnlyInputValue(initialHash)
    }
  }, [initialHash])

  const searchInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (searchInput.current) {
      searchInput.current.focus()
    }
  }, [openModal])

  const onDisplayChangeHandler: ChangeEventHandler<HTMLInputElement> = useCallback(
    event => {
      setOpenSearchModal(true)
      setCaptureIntent(false)
      logCdfEvent(CustomerSupportSearchEnterText({}))
      setQuery(event.target.value)
    },
    [logCdfEvent]
  )

  const onFocusHandler = () => {
    setCaptureIntent(true)
    logCdfEvent(CustomerSupportSearchStart({}))
  }

  const onQueryChangeHandler: ChangeEventHandler<HTMLInputElement> = useCallback(
    event => {
      if (captureIntent) {
        setCaptureIntent(false)
        logCdfEvent(CustomerSupportSearchEnterText({}))
      }
      const newQuery = event.target.value
      setQuery(newQuery)

      onQueryChange?.(newQuery)
    },
    [captureIntent, logCdfEvent, onQueryChange]
  )

  const onSearchModalClose = () => {
    setReadOnlyInputValue(query)
    setOpenSearchModal(false)
  }

  const searchPlaceholder = t('Example: PIN reset', {
    context: 'search placeholder',
    nsSeparator: false,
  })
  const shouldShowMoreResults = showMoreResults && (data?.total_result_count ?? 0) > MAX_DISPLAYED_RESULTS

  const getSearchResultContent = () => {
    if (isLoading) {
      return (
        <LoadingContainer>
          <div data-testid="loading-icon">
            <ProgressCircular size="large" />
          </div>
        </LoadingContainer>
      )
    }

    if (isError) {
      return (
        <ResultsContainer>
          <SearchResults
            results={[]} // No results when error occurs
            query={query}
            showMoreResults={false}
            isErrored
            errorMessage={data?.error_message}
            Link={Link}
            t={t}
          />
        </ResultsContainer>
      )
    }

    if (data) {
      return (
        <ResultsContainer>
          <SearchResults
            results={data.results ?? []}
            query={query}
            showMoreResults={shouldShowMoreResults}
            isErrored={isError}
            errorMessage={data?.error_message}
            Link={Link}
            t={t}
          />
        </ResultsContainer>
      )
    }

    if (recommendedArticles && recommendedArticles?.length > 0) {
      return (
        <>
          <TopArticleHeader variant="sectionTitle">
            {t('{recommendedArticleTitle}', {
              context: 'Recommended articles when performing a search',
              recommendedArticleTitle: recommendedArticleTitle,
            })}
          </TopArticleHeader>
          <ResultsContainer>
            <SearchResults
              results={recommendedArticles}
              query={query}
              showMoreResults={shouldShowMoreResults}
              isErrored={false}
              Link={Link}
              t={t}
            />
          </ResultsContainer>
        </>
      )
    }

    return (
      <IconContainer data-testid="icon-container">{darkMode ? <TelescopeDarkIcon /> : <TelescopeIcon />}</IconContainer>
    )
  }

  return (
    <>
      <StyledSearchInput
        invert={invert}
        onFocus={onFocusHandler}
        placeholder={searchPlaceholder}
        name="search" // Prevents Safari on iOS prompting to autofill username
        type="search"
        onChange={onDisplayChangeHandler}
        value={readOnlyInputValue}
      />
      {openModal && (
        <Modal fixed onClose={onSearchModalClose} width="45rem">
          <ModalContent>
            <StyledSearchInput
              data-testid="modal-input"
              invert
              ref={searchInput}
              placeholder={searchPlaceholder}
              name="search"
              type="search"
              value={query}
              onChange={onQueryChangeHandler}
            />
            {getSearchResultContent()}
          </ModalContent>
        </Modal>
      )}
    </>
  )
}

export default Search
